import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';
import { Link, createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/auction/')({
  component: AuctionPage,
});

function AuctionPage() {
  const toc = [
    { id: 'auction-overview', label: 'Overview' },
    { id: 'auction-bidding', label: 'Bidding Rules' },
    { id: 'auction-paying', label: 'Paying For Bids' },
    { id: 'auction-sitting', label: 'Sitting' },
    { id: 'auction-outcomes', label: 'Winning & Outcomes' },
    { id: 'auction-related', label: 'Related Pages' },
  ];

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="auction-overview" title="Auction">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <img
            src="/images/misc/the_arcane_market@2x.png"
            alt="Auction"
            className="w-full max-w-[120px] rounded-xl border border-[#d4af37]/25"
          />
          <div className="space-y-3">
            <p>
              The world auction sells one featured item at a time. It is where
              rare goods enter circulation and where the loudest bidding wars
              usually begin.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge>World auction</Badge>
              <Badge>Rare item flow</Badge>
              <Badge>Kingdom planning</Badge>
            </div>
          </div>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="auction-bidding" title="Bidding Rules">
        <div className="grid gap-3 lg:grid-cols-2">
          {[
            ['Opening bid', 'The first valid bid must be at least 150 gems.'],
            [
              'Minimum raise',
              'Once there is a leader, the next bid must beat the current bid by at least 50 gems or 5%, whichever is larger.',
            ],
            [
              'Time extension',
              'A strong enough live bid can push the end time back, giving other provinces time to answer.',
            ],
            [
              'No self-overbid',
              'If your province is already the current winner, you cannot bid again just to raise your own price.',
            ],
          ].map(([title, description]) => (
            <Card key={title} className="p-4">
              <h3 className="font-cinzel text-lg text-[#f0f0f0]">{title}</h3>
              <p className="text-sm text-[#b0b0b0]">{description}</p>
            </Card>
          ))}
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="auction-paying" title="Paying For Bids">
        <p>
          Auction buying power comes from two pools combined together: the
          province&apos;s reward gems and the acting account&apos;s permanent gems.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Reward gems belong to the province and are short-lived.</li>
          <li>Permanent gems belong to the account and carry across ages.</li>
          <li>Reward gems are spent before permanent gems.</li>
        </ul>
        <p>
          In practice, that means auction planning is often a mix of province
          performance and account-level support. Selling servants or aiding them
          between kingdom mates can be part of that prep work.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="auction-sitting" title="Auction While Sitting">
        <p>
          Sitting changes who supplies the permanent gem side of a bid. The bid
          is still placed for the sat province, but the permanent gem portion
          comes from the sitter&apos;s account while the reward gem portion comes
          from the sat province.
        </p>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
          <p className="text-sm text-[#b8b8b8]">
            In other words: when a sitter bids, the province target stays the
            same, but bid eligibility is calculated from sitter account
            permanent gems plus the sat province&apos;s reward gems.
          </p>
        </div>
        <p>
          That is why servant aid and pre-arranged gem support matter before
          sitting handoffs. If a kingdom expects a sitter to manage auction
          timing, make sure the right account actually holds the permanent gems
          needed to do it.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="auction-outcomes" title="Winning and Outcomes">
        <p>
          Winning does not always mean the item goes straight onto the province.
          If the province can equip the item, it is awarded there. If it
          cannot, the item is sold into that kingdom&apos;s market instead.
        </p>
        <p>
          That makes auction wins useful even when the bidder is not the final
          holder. Some kingdoms use the auction to seed market stock for a
          later internal transfer or purchase decision.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="auction-related" title="Related Pages">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              to: '/market',
              label: 'The Market',
              description: 'Kingdom stock, item buying, and price context.',
            },
            {
              to: '/gems',
              label: 'Servants & Gems',
              description: 'Permanent gem setup and servant conversion.',
            },
            {
              to: '/trade',
              label: 'Trade & Aid',
              description: 'Kingdom handoffs, including servant support.',
            },
            {
              to: '/classic-guide/gems-market',
              label: 'Classic Market Guide',
              description: 'Original in-game auction and market wording.',
            },
          ].map((item) => (
            <Link key={item.to} to={item.to}>
              <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
                <h3 className="font-cinzel text-lg text-[#f0f0f0]">
                  {item.label}
                </h3>
                <p className="mt-2 text-sm text-[#b0b0b0]">
                  {item.description}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>
    </div>
  );
}
